import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, Checkbox, FormControl, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import StyledCard from '../../../../components/common/StyledCard';
import {
    arrayOnlyFansSubscriberHostessAutoReplyFreeloadersAction,
    OnlyFansSubscriberHostessAutoReplyExpired,
} from '../../../../types/hostessAutoReplyFreeloaders';
import { titleCase } from '../../../../utils/common';
import SettingRow from './SettingRow';
import SettingsSectionHeader from './SettingsSectionHeader';

const checkboxIconSx = {
    '& .MuiSvgIcon-root': {
        fontSize: '1.5rem',
    },
};

const AutoReplyToExpiredSettingsCard = (props: {
    formData: OnlyFansSubscriberHostessAutoReplyExpired;
    setFormData: (data: OnlyFansSubscriberHostessAutoReplyExpired) => void;
    autoReplyExpired: OnlyFansSubscriberHostessAutoReplyExpired | null;
    historyTtlEnabled: boolean;
    setHistoryTtlEnabled: (historyTTLEnabled: boolean) => void;
    historyTtlDaysString: string;
    setHistoryTtlDaysString: (historyTTLAmountString: string) => void;
}) => {
    const { formData, setFormData, historyTtlEnabled, setHistoryTtlEnabled, historyTtlDaysString, setHistoryTtlDaysString } = props;

    return (
        <>
            <StyledCard>
                <SettingsSectionHeader icon={PeopleAltOutlinedIcon} title="Whom to Skip" />

                <SettingRow
                    title="Skip Creators?"
                    description="If checked, auto-reply will not reply to creators"
                    last
                    control={
                        <Checkbox
                            checked={formData && formData.excludeCreators ? true : false}
                            size="medium"
                            onChange={e => {
                                setFormData({ ...formData, excludeCreators: e.target.checked });
                            }}
                            sx={checkboxIconSx}
                        />
                    }
                />
            </StyledCard>

            <StyledCard>
                <SettingsSectionHeader icon={SettingsOutlinedIcon} title="Action to Take" />

                <SettingRow
                    title="Action to Take on Last Reply"
                    description="What to do when the freeloader has received the maximum number of strikes (the last reply)"
                    wide
                    control={
                        <Box sx={{ width: { xs: '100%', sm: 140 } }}>
                            <FormControl fullWidth>
                                <InputLabel id="action">Action</InputLabel>
                                <Select
                                    labelId="action"
                                    defaultValue={formData ? formData.action : 'none'}
                                    value={formData && formData.action ? formData.action : 'none'}
                                    onChange={e => {
                                        setFormData({ ...formData, action: e.target.value });
                                    }}
                                    fullWidth
                                    size="medium"
                                    label="Action"
                                >
                                    {arrayOnlyFansSubscriberHostessAutoReplyFreeloadersAction.map(item => (
                                        <MenuItem value={item} key={item}>
                                            {titleCase(item)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    }
                />

                <SettingRow
                    title="Auto Unmute/Unrestrict on Follow/Spending?"
                    description="If checked, fans will be unmuted or unrestricted as necessary when they re-follow you or spend money."
                    last
                    control={
                        <Checkbox
                            checked={formData && formData.autoUnrestrict ? true : false}
                            disabled={formData && formData.action !== 'restrict' && formData.action !== 'mute' ? true : false}
                            size="medium"
                            onChange={e => {
                                setFormData({ ...formData, autoUnrestrict: e.target.checked });
                            }}
                            sx={checkboxIconSx}
                        />
                    }
                />
            </StyledCard>

            <StyledCard>
                <SettingsSectionHeader icon={EmailOutlinedIcon} title="Message Settings" />

                <SettingRow
                    title="Delay Before Sending Reply?"
                    description="If checked, the reply will be sent a random 1-2 minutes after the fan sent the message."
                    control={
                        <Checkbox
                            checked={formData && formData.delayBeforeSendingMessage ? true : false}
                            size="medium"
                            onChange={e => {
                                setFormData({ ...formData, delayBeforeSendingMessage: e.target.checked });
                            }}
                            sx={checkboxIconSx}
                        />
                    }
                />

                <SettingRow
                    title="Send Reply On Purchases?"
                    description="Normally, the system does not reply when the fan purchases something in the chat, but if checked, the system will send a reply. This is useful if you want to remind the user that he is current expired and missing out on the good stuff."
                    control={
                        <Checkbox
                            checked={formData && formData.sendReplyOnPurchase ? true : false}
                            size="medium"
                            onChange={e => {
                                setFormData({ ...formData, sendReplyOnPurchase: e.target.checked });
                            }}
                            sx={checkboxIconSx}
                        />
                    }
                />

                <SettingRow
                    title="Repeat Replies When Script Finished?"
                    description="If checked, the script will be repeated from the start when the fan has received the last reply and they message again."
                    control={
                        <Checkbox
                            checked={formData && formData.repeatScriptWhenFinished ? true : false}
                            size="medium"
                            onChange={e => {
                                setFormData({ ...formData, repeatScriptWhenFinished: e.target.checked });
                            }}
                            sx={checkboxIconSx}
                        />
                    }
                />

                <SettingRow
                    title="Repeat Script After X Days?"
                    description="If checked, the script will be repeated from the start when the fan messages after the specified number of days."
                    last
                    control={
                        <>
                            <Checkbox
                                checked={historyTtlEnabled}
                                size="medium"
                                onChange={e => {
                                    setHistoryTtlEnabled(e.target.checked);

                                    if (!e.target.checked) {
                                        setFormData({
                                            ...formData,
                                            historyTtlDays: null,
                                        });
                                        setHistoryTtlDaysString('0');
                                    } else {
                                        setFormData({
                                            ...formData,
                                            historyTtlDays: 30,
                                        });
                                        setHistoryTtlDaysString('30');
                                    }
                                }}
                                sx={checkboxIconSx}
                            />
                            <FormControl>
                                <InputLabel htmlFor="historyTtlDays">Days</InputLabel>
                                <OutlinedInput
                                    id="historyTtlDays"
                                    size="medium"
                                    inputProps={{ inputMode: 'text', pattern: /^\d*$$/ }}
                                    disabled={!historyTtlEnabled}
                                    value={historyTtlDaysString}
                                    onChange={e => {
                                        e.preventDefault();

                                        if (/^\d*$/.test(e.target.value)) {
                                            const parsed = parseFloat(e.target.value.replaceAll(',', '.'));

                                            if (isNaN(parsed) || parsed === 0) {
                                                setFormData({ ...formData, historyTtlDays: null });
                                                setHistoryTtlDaysString('0');
                                            } else {
                                                setFormData({ ...formData, historyTtlDays: parsed });
                                                setHistoryTtlDaysString(e.target.value.replace(/^0+/, ''));
                                            }
                                        }
                                    }}
                                    label="Length"
                                    error={formData && historyTtlEnabled && !formData.historyTtlDays ? true : false}
                                    sx={{
                                        width: '80px',
                                    }}
                                />
                            </FormControl>
                        </>
                    }
                />
            </StyledCard>
        </>
    );
};

export default AutoReplyToExpiredSettingsCard;
